import React, { useState } from 'react';
import { supabase } from './supabaseClient';
import './RegistrationPage.css';

const RegistrationPage = ({ onRegistered, onGoToLogin }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  
  // Состояния чекбоксов
  const [isMinor, setIsMinor] = useState(false);
  const [isLegalRep, setIsLegalRep] = useState(false);
  const [hasReadPolicy, setHasReadPolicy] = useState(false);
  const [consentPolicy, setConsentPolicy] = useState(false);
  const [consentPersonalData, setConsentPersonalData] = useState(false);
  const [consentAI, setConsentAI] = useState(false); 
  
  // Состояния для видимости паролей (глазик)
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleOpenPolicy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPolicyModal(true);
  };

  const handleClosePolicy = () => {
    setShowPolicyModal(false);
    setHasReadPolicy(true);
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Введите ФИО';

    if (!formData.email.trim()) newErrors.email = 'Введите email';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Некорректный формат email';
    
    if (!formData.password) newErrors.password = 'Введите пароль';
    else if (formData.password.length < 6) newErrors.password = 'Пароль должен содержать не менее 6 символов';
    else if (/[а-яА-ЯёЁ\s<>&'"\\\/]/.test(formData.password)) {
      newErrors.password = 'Запрещенные символы: кириллица, пробелы, < > & \' " \\ /';
    }
    
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Подтвердите пароль';
    else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают';
    }

    if (isMinor && !isLegalRep) {
      newErrors.legalRep = 'Необходимо подтвердить статус законного представителя';
    }
    if (!consentPolicy) {
      newErrors.consentPolicy = 'Необходимо подтвердить ознакомление с Политикой';
    }
    if (!consentPersonalData) {
      newErrors.consentPersonalData = 'Необходимо дать согласие на обработку персональных данных';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);

    try {
      // The password goes to GoTrue, which hashes it. The profile fields ride
      // along as user metadata and are copied into speech_assessment.students by
      // the on_auth_user_created trigger.
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.name,
            is_minor: isMinor,
            is_legal_rep: isLegalRep,
            consent_policy: consentPolicy,
            consent_personal_data: consentPersonalData,
            consent_ai: consentAI,
          },
        },
      });

      if (error) {
        const alreadyRegistered =
          error.code === 'user_already_exists' || /already registered/i.test(error.message || '');
        setErrors(prev => ({
          ...prev,
          email: alreadyRegistered
            ? 'Пользователь с таким email уже зарегистрирован'
            : 'Не удалось создать аккаунт: ' + error.message,
        }));
      } else if (data.session) {
        if (onRegistered) onRegistered(data.session);
      } else {
        // The server is expected to run with GOTRUE_MAILER_AUTOCONFIRM=true, so
        // signUp should hand back a session. No session means confirmation is
        // still switched on; say so instead of dropping the user on a
        // logged-out page with no explanation.
        setErrors(prev => ({
          ...prev,
          email: 'Аккаунт создан, но вход не выполнен: на сервере включено подтверждение email.',
        }));
      }
    } catch (err) {
      console.error('Непредвиденная ошибка:', err);
      setErrors(prev => ({ ...prev, email: 'Не удалось создать аккаунт. Попробуйте позже.' }));
    } finally {
      setIsLoading(false);
    }
  };

  const PolicyLink = () => (
    <a href="#policy" onClick={handleOpenPolicy} className="policy-link">Политике конфиденциальности</a>
  );

  return (
    <div className="reg-container">
      <div className="reg-card">
        <div className="reg-header">
          <h1 className="reg-title">Регистрация</h1>
          <p className="reg-subtitle">Создайте аккаунт для доступа к системе тестирования</p>
        </div>

        <form onSubmit={handleSubmit} className="reg-form">
          
          <div className="reg-input-group">
            <label>ФИО <span className="required-star">*</span></label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Иванов Иван Иванович" className={errors.name ? 'input-error' : ''} />
            {errors.name && <div className="error-tooltip"><span className="error-icon">⚠️</span>{errors.name}</div>}
          </div>

          <div className="reg-input-group">
            <label>Email <span className="required-star">*</span></label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="email@example.com" className={errors.email ? 'input-error' : ''} />
            {errors.email && <div className="error-tooltip"><span className="error-icon">⚠️</span>{errors.email}</div>}
          </div>

          <div className="reg-input-group">
            <label>Пароль <span className="required-star">*</span></label>
            <div className="password-wrapper">
              <input 
                type={showPassword ? "text" : "password"} 
                name="password" 
                value={formData.password} 
                onChange={handleChange} 
                placeholder="Минимум 6 символов" 
                className={errors.password ? 'input-error' : ''} 
              />
              <button type="button" className="eye-btn" onClick={() => setShowPassword(p => !p)}>
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.password && <div className="error-tooltip"><span className="error-icon">⚠️</span>{errors.password}</div>}
          </div>

          <div className="reg-input-group">
            <label>Подтверждение пароля <span className="required-star">*</span></label>
            <div className="password-wrapper">
              <input 
                type={showConfirmPassword ? "text" : "password"} 
                name="confirmPassword" 
                value={formData.confirmPassword} 
                onChange={handleChange} 
                placeholder="Повторите пароль" 
                className={errors.confirmPassword ? 'input-error' : ''} 
              />
              <button type="button" className="eye-btn" onClick={() => setShowConfirmPassword(p => !p)}>
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.confirmPassword && <div className="error-tooltip"><span className="error-icon">⚠️</span>{errors.confirmPassword}</div>}
          </div>

          <div className="reg-checkboxes">
            
            <label className="reg-checkbox-label">
              <input type="checkbox" checked={isMinor} onChange={(e) => { setIsMinor(e.target.checked); setIsLegalRep(false); if(errors.legalRep) setErrors(prev => ({...prev, legalRep: null})); }} className="reg-checkbox" />
              <span>Заниматься на платформе будет несовершеннолетний пользователь?</span>
            </label>

            {isMinor && (
              <label className="reg-checkbox-label long-text-label">
                <input type="checkbox" checked={isLegalRep} onChange={(e) => { setIsLegalRep(e.target.checked); if(errors.legalRep) setErrors(prev => ({...prev, legalRep: null})); }} className="reg-checkbox" />
                <span>Подтверждаю, что я являюсь законным представителем несовершеннолетнего пользователя и даю согласие ООО «Виртуальный логопед», на обработку персональных данных несовершеннолетнего пользователя в объёме и в соответствии с целями, указанными в <PolicyLink />.</span>
              </label>
            )}
            {errors.legalRep && <div className="error-tooltip"><span className="error-icon">⚠️</span>{errors.legalRep}</div>}

            <div className={`consent-block ${!hasReadPolicy ? 'disabled-consent' : ''}`}>
              <label className="reg-checkbox-label">
                <input type="checkbox" checked={consentPolicy} onChange={(e) => { setConsentPolicy(e.target.checked); if(errors.consentPolicy) setErrors(prev => ({...prev, consentPolicy: null})); }} disabled={!hasReadPolicy} className="reg-checkbox" />
                <span>Подтверждаю, что я ознакомился с <PolicyLink /></span>
              </label>
              {!hasReadPolicy && <div className="consent-hint">⚠️ Ознакомьтесь с текстом по ссылке, чтобы поставить галочку</div>}
            </div>
            {errors.consentPolicy && <div className="error-tooltip"><span className="error-icon">⚠️</span>{errors.consentPolicy}</div>}

            <label className="reg-checkbox-label long-text-label">
              <input type="checkbox" checked={consentPersonalData} onChange={(e) => { setConsentPersonalData(e.target.checked); if(errors.consentPersonalData) setErrors(prev => ({...prev, consentPersonalData: null})); }} className="reg-checkbox" />
              <span>Подтверждаю, что я даю согласие ООО «Виртуальный логопед» на обработку персональных данных, на платформе информационно-логопедических услуг, доступной по адресу https://ezspeech.ai/, в объёме и в соответствии с целями, указанными в <PolicyLink />.</span>
            </label>
            {errors.consentPersonalData && <div className="error-tooltip"><span className="error-icon">⚠️</span>{errors.consentPersonalData}</div>}

            <label className="reg-checkbox-label long-text-label">
              <input type="checkbox" checked={consentAI} onChange={(e) => setConsentAI(e.target.checked)} className="reg-checkbox" />
              <span>
                Подтверждаю, что я даю согласие на использование обезличенных данных по выполнению упражнений (аудио и видеоматериалы) на платформе для обучения нейросетевых алгоритмов и моделей ИИ 
                <span className="optional-tag">(добровольно)</span>
              </span>
            </label>

          </div>

          <button type="submit" className="reg-submit-btn" disabled={isLoading}>
            {isLoading ? 'Создание аккаунта...' : 'Зарегистрироваться'}
          </button>
        </form>

        <div className="reg-footer">
          <span>Уже есть аккаунт?</span>
          <button className="reg-login-link" onClick={onGoToLogin}>Войти</button>
        </div>
      </div>

      {showPolicyModal && (
        <div className="policy-modal-overlay" onClick={handleClosePolicy}>
          <div className="policy-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="policy-modal-header">
              <h2>Политика конфиденциальности</h2>
              <button className="policy-close-btn" onClick={handleClosePolicy}>✕</button>
            </div>
                        <div className="policy-modal-body">
              <p style={{ marginBottom: '20px' }}>
                Пожалуйста, ознакомьтесь с Политикой конфиденциальности ООО «Виртуальный логопед». 
                Она откроется в новой вкладке.
              </p>
              <a 
                href="/privacy-policy.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  display: 'inline-block', 
                  padding: '10px 20px', 
                  background: '#312e81', 
                  color: 'white', 
                  borderRadius: '8px', 
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Открыть Политику (PDF)
              </a>
            </div>
            <button className="policy-accept-btn" onClick={handleClosePolicy}>Я ознакомлен(а)</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationPage;